import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Translation, TranslocoLoader } from '@jsverse/transloco';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
    private _httpClient = inject(HttpClient);
    private readonly CACHE_KEY_PREFIX = 'transloco_cache_';
    private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get translation with caching
     * ✅ Caches translations in localStorage to avoid reloading on every app start
     *
     * @param lang
     */
    getTranslation(lang: string): Observable<Translation> {
        // Check cache first
        const cached = this.getCachedTranslation(lang);
        if (cached) {
            return of(cached);
        }

        // Load from server and cache
        return this._httpClient.get<Translation>(`./i18n/${lang}.json`).pipe(
            tap((translation) => {
                // Cache the translation
                this.cacheTranslation(lang, translation);
            }),
            catchError((error) => {
                console.error(`Error loading translation for ${lang}:`, error);
                // Try to return stale cache on error
                const staleCache = this.getCachedTranslation(lang, true);
                if (staleCache) {
                    return of(staleCache);
                }
                throw error;
            })
        );
    }

    /**
     * Get cached translation from localStorage
     */
    private getCachedTranslation(lang: string, allowStale: boolean = false): Translation | null {
        try {
            const cacheKey = `${this.CACHE_KEY_PREFIX}${lang}`;
            const cached = localStorage.getItem(cacheKey);
            if (!cached) return null;

            const { translation, timestamp } = JSON.parse(cached);
            const age = Date.now() - timestamp;

            if (allowStale || age < this.CACHE_DURATION) {
                return translation;
            }

            // Cache expired, remove it
            localStorage.removeItem(cacheKey);
            return null;
        } catch (error) {
            console.error('Error reading translation cache:', error);
            return null;
        }
    }

    /**
     * Cache translation in localStorage
     */
    private cacheTranslation(lang: string, translation: Translation): void {
        try {
            const cacheKey = `${this.CACHE_KEY_PREFIX}${lang}`;
            const cacheData = {
                translation,
                timestamp: Date.now()
            };
            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        } catch (error) {
            console.error('Error caching translation:', error);
            // If localStorage is full, try to clear old caches
            this.clearOldCaches();
        }
    }

    /**
     * Clear old translation caches if storage is full
     */
    private clearOldCaches(): void {
        try {
            const now = Date.now();
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith(this.CACHE_KEY_PREFIX)) {
                    try {
                        const cached = localStorage.getItem(key);
                        if (cached) {
                            const { timestamp } = JSON.parse(cached);
                            if (now - timestamp > this.CACHE_DURATION) {
                                localStorage.removeItem(key);
                            }
                        }
                    } catch (e) {
                        // Remove invalid cache entries
                        localStorage.removeItem(key);
                    }
                }
            });
        } catch (error) {
            console.error('Error clearing old caches:', error);
        }
    }
}
